import { GomarkePixelConfig } from '../../types';
import { BaseTracker } from './BaseTracker';
import { PageViewTracker } from './pageView';
import { PurchaseTracker } from './purchase';
import { LeadTracker } from './lead';
import { AddToCartTracker } from './addToCart';
import { InitiateCheckoutTracker } from './initiateCheckout';
import { AddPaymentInfoTracker } from './addPaymentInfo';
import { RemoveToCartTracker } from './removeToCart';
import { SearchTracker } from './search';
import { ViewContentTracker } from './viewContent';

/**
 * Factory para criar e gerenciar instâncias dos trackers
 */
export class TrackerFactory {
  private trackers: Map<string, BaseTracker> = new Map();
  private config: GomarkePixelConfig;
  private sessionId: string;
  private version: string;
  private visitorId: string;
  constructor(
    config: GomarkePixelConfig,
    sessionId: string,
    visitorId: string,
    version: string
  ) {
    this.config = config;
    this.sessionId = sessionId;
    this.version = version;
    this.visitorId = visitorId;
  }

  /**
   * Obtém ou cria uma instância do PageViewTracker
   */
  public getPageViewTracker(): PageViewTracker {
    const key = 'pageView';
    if (!this.trackers.has(key)) {
      this.trackers.set(
        key,
        new PageViewTracker(
          this.config,
          this.sessionId,
          this.visitorId,
          this.version
        )
      );
    }
    return this.trackers.get(key) as PageViewTracker;
  }

  /**
   * Obtém ou cria uma instância do PurchaseTracker
   */
  public getPurchaseTracker(): PurchaseTracker {
    const key = 'purchase';
    if (!this.trackers.has(key)) {
      this.trackers.set(
        key,
        new PurchaseTracker(
          this.config,
          this.sessionId,
          this.visitorId,
          this.version
        )
      );
    }
    return this.trackers.get(key) as PurchaseTracker;
  }

  /**
   * Obtém ou cria uma instância do LeadTracker
   */
  public getLeadTracker(): LeadTracker {
    const key = 'lead';
    if (!this.trackers.has(key)) {
      this.trackers.set(
        key,
        new LeadTracker(
          this.config,
          this.sessionId,
          this.visitorId,
          this.version
        )
      );
    }
    return this.trackers.get(key) as LeadTracker;
  }

  /**
   * Obtém ou cria uma instância do AddToCartTracker
   */
  public getAddToCartTracker(): AddToCartTracker {
    const key = 'addToCart';
    if (!this.trackers.has(key)) {
      this.trackers.set(
        key,
        new AddToCartTracker(
          this.config,
          this.sessionId,
          this.visitorId,
          this.version
        )
      );
    }
    return this.trackers.get(key) as AddToCartTracker;
  }

  /**
   * Obtém ou cria uma instância do InitiateCheckoutTracker
   */
  public getInitiateCheckoutTracker(): InitiateCheckoutTracker {
    const key = 'initiateCheckout';
    if (!this.trackers.has(key)) {
      this.trackers.set(
        key,
        new InitiateCheckoutTracker(
          this.config,
          this.sessionId,
          this.visitorId,
          this.version
        )
      );
    }
    return this.trackers.get(key) as InitiateCheckoutTracker;
  }

  /**
   * Obtém ou cria uma instância do AddPaymentInfoTracker
   */
  public getAddPaymentInfoTracker(): AddPaymentInfoTracker {
    const key = 'addPaymentInfo';
    if (!this.trackers.has(key)) {
      this.trackers.set(
        key,
        new AddPaymentInfoTracker(
          this.config,
          this.sessionId,
          this.visitorId,
          this.version
        )
      );
    }
    return this.trackers.get(key) as AddPaymentInfoTracker;
  }

  /**
   * Obtém ou cria uma instância do RemoveToCartTracker
   */
  public getRemoveToCartTracker(): RemoveToCartTracker {
    const key = 'removeToCart';
    if (!this.trackers.has(key)) {
      this.trackers.set(
        key,
        new RemoveToCartTracker(
          this.config,
          this.sessionId,
          this.visitorId,
          this.version
        )
      );
    }
    return this.trackers.get(key) as RemoveToCartTracker;
  }

  /**
   * Obtém ou cria uma instância do SearchTracker
   */
  public getSearchTracker(): SearchTracker {
    const key = 'search';
    if (!this.trackers.has(key)) {
      this.trackers.set(
        key,
        new SearchTracker(
          this.config,
          this.sessionId,
          this.visitorId,
          this.version
        )
      );
    }
    return this.trackers.get(key) as SearchTracker;
  }

  /**
   * Obtém ou cria uma instância do ViewContentTracker
   */
  public getViewContentTracker(): ViewContentTracker {
    const key = 'viewContent';
    if (!this.trackers.has(key)) {
      this.trackers.set(
        key,
        new ViewContentTracker(
          this.config,
          this.sessionId,
          this.visitorId,
          this.version
        )
      );
    }
    return this.trackers.get(key) as ViewContentTracker;
  }

  /**
   * Atualiza a configuração de todos os trackers
   */
  public updateConfig(newConfig: Partial<GomarkePixelConfig>): void {
    this.config = { ...this.config, ...newConfig };

    // Recria todos os trackers com a nova configuração
    this.trackers.clear();
  }

  /**
   * Atualiza o sessionId (ex.: após init do Core Engine)
   */
  public updateSessionId(sessionId: string): void {
    this.sessionId = sessionId;
    this.trackers.clear();
  }

  /**
   * Atualiza o visitorId (ex.: após init do Core Engine)
   */
  public updateVisitorId(visitorId: string): void {
    this.visitorId = visitorId;
    this.trackers.clear();
  }

  /**
   * Obtém estatísticas dos trackers
   */
  public getTrackerStats(): { totalTrackers: number; trackerTypes: string[] } {
    return {
      totalTrackers: this.trackers.size,
      trackerTypes: Array.from(this.trackers.keys()),
    };
  }

  /**
   * Limpa todos os trackers da memória
   */
  public clearTrackers(): void {
    this.trackers.clear();
  }
}
